import { hashString32 } from '../sim/rng';
import { Tile } from '../sim/grid';
import type { LevelDef } from './levelSchema';

/**
 * Each lawn's challenge star (docs/GDD.md §8): the third star, after par and all coins. Picked to
 * suit the lawn from what is on it, so neighboring lawns ask for different things and every lawn
 * has a reason to be replayed.
 */
export type ChallengeId =
  'flawless' | 'oneGo' | 'tidy' | 'gentle' | 'noTools' | 'noBoosts' | 'gold';

export const CHALLENGE_TEXT: Readonly<Record<ChallengeId, string>> = {
  flawless: 'Lose no hearts',
  oneGo: 'Clear it in one go',
  tidy: 'Damage nothing',
  gentle: 'Never bump a critter',
  noTools: 'Use no tools',
  noBoosts: 'Use no boosts',
  // (The level card and results name the time: src/game/starGoals.ts.)
  gold: 'Beat the gold time',
};

/**
 * From this lawn on, the challenges get harder (owner, 2026-10-31): "in one go" and "no tools" cost
 * a capable player nothing by then, so they give way to "no boosts" and the gold time.
 */
export const LATE_CHALLENGES_FROM = 101;

/** Tiles a mower can damage on its way (flowers and the green, decorations, hoses, sprinklers). */
const DAMAGEABLE: readonly Tile[] = [Tile.FlowerBed, Tile.Ornament, Tile.Hose, Tile.Sprinkler];

/**
 * The lawn's challenge: "damage nothing" where there is something to damage and "never bump a
 * critter" where there are critters (each twice as likely), otherwise (or as well) flawless, in
 * one go, or without tools. Deterministic per lawn.
 */
export function challengeFor(level: LevelDef): ChallengeId {
  const damageable = level.sim.tiles.some((t) => DAMAGEABLE.includes(t));
  const critters = (level.sim.critters ?? []).some((c) => c.kind !== 'sheep');
  const late = (level.level ?? 0) >= LATE_CHALLENGES_FROM;
  const suited: ChallengeId[] = [];
  // Late lawns: "damage nothing" weighted up (it is the hard one on a mower that smashes).
  if (damageable)
    suited.push(...((late ? ['tidy', 'tidy', 'tidy'] : ['tidy', 'tidy']) as ChallengeId[]));
  if (critters) suited.push('gentle', 'gentle');
  const pool: ChallengeId[] = late
    ? [...suited, 'flawless', 'noBoosts', ...(level.goldTime ? ['gold' as const] : [])]
    : [...suited, 'flawless', 'oneGo', 'noTools'];
  return pool[hashString32(`${level.id}:challenge`) % pool.length]!;
}
